'use client';

import type { HTMLMotionProps, Variants } from 'motion/react';
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import {
  AnimatedButton,
  type ButtonSize,
  type ButtonVariant,
} from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Bookmark02Icon, Cancel01Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
import { MOTION_ENTRANCE } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Animation config (hoisted to module scope per React Compiler rules)
// Approach follows chanhdai.com/components/copy-button — shared variants
// object spread across all icon states, popLayout for crossfade.
// Uses project duration tokens (200ms entrance, 150ms exit).
// ---------------------------------------------------------------------------

const iconVariants: Variants = {
  initial: { opacity: 0, scale: 0.8, filter: 'blur(2px)' },
  animate: { opacity: 1, scale: 1, filter: 'blur(0px)' },
  exit: { opacity: 0, scale: 0.8 },
};

const motionIconProps: HTMLMotionProps<'span'> = {
  variants: iconVariants,
  initial: 'initial',
  animate: 'animate',
  exit: 'exit',
  transition: MOTION_ENTRANCE,
  className: 'inline-flex items-center justify-center',
};

const CONFIRM_DISPLAY_MS = 600;

/**
 * Square footprint per button size — mirrors AnimatedButton's
 * `iconOnlySizeClasses` so the bookmark lines up with sibling icon-only
 * buttons (copy / share / info). AnimatedButton can't auto-detect icon-only
 * here because the glyph is wrapped in motion spans for the crossfade rather
 * than passed as a bare `<Icon>` child.
 */
const SQUARE_SIZE: Record<ButtonSize, string> = {
  xs: 'size-6',
  sm: 'size-8',
  default: 'size-9',
  lg: 'size-10',
  xl: 'size-12',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AnimatedBookmarkProps {
  isSaved: boolean;
  onClick: (e: React.MouseEvent) => void;
  ariaLabel: string;
  className?: string;
  size?: number;
  /** Underlying button variant. @default 'tertiary' */
  variant?: ButtonVariant;
  /** Underlying button size — controls the square footprint. @default 'sm' */
  buttonSize?: ButtonSize;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type Phase = 'idle' | 'saving' | 'removing';

export function AnimatedBookmark({
  isSaved,
  onClick,
  ariaLabel,
  className,
  size = 14,
  variant = 'tertiary',
  buttonSize = 'sm',
}: AnimatedBookmarkProps): React.ReactNode {
  const prefersReduced = useReducedMotion();
  const [phase, setPhase] = useState<Phase>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const squareClass = SQUARE_SIZE[buttonSize];

  useEffect((): (() => void) => {
    const timer = timerRef;
    return (): void => {
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);

  function handleClick(e: React.MouseEvent): void {
    onClick(e);

    if (prefersReduced) return;

    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    setPhase(isSaved ? 'removing' : 'saving');

    timerRef.current = setTimeout(() => {
      setPhase('idle');
      timerRef.current = null;
    }, CONFIRM_DISPLAY_MS);
  }

  if (prefersReduced) {
    return (
      <AnimatedButton
        variant={variant}
        size={buttonSize}
        onClick={handleClick}
        aria-label={ariaLabel}
        className={cn(squareClass, 'p-0', className)}
      >
        <Icon icon={Bookmark02Icon} className={isSaved ? 'fill-current' : ''} size={size} />
      </AnimatedButton>
    );
  }

  return (
    <LazyMotion features={domAnimation}>
      <AnimatedButton
        variant={variant}
        size={buttonSize}
        onClick={handleClick}
        aria-label={ariaLabel}
        className={cn('relative', squareClass, 'p-0', className)}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {phase === 'saving' ? (
            <m.span key="check" {...motionIconProps}>
              <Icon
                icon={Tick02Icon}
                size={size}
                strokeWidth={3}
                className="text-foreground-primary"
              />
            </m.span>
          ) : phase === 'removing' ? (
            <m.span key="x" {...motionIconProps}>
              <Icon
                icon={Cancel01Icon}
                size={size}
                strokeWidth={3}
                className="text-foreground-primary"
              />
            </m.span>
          ) : (
            <m.span key="bookmark" {...motionIconProps}>
              <Icon icon={Bookmark02Icon} className={isSaved ? 'fill-current' : ''} size={size} />
            </m.span>
          )}
        </AnimatePresence>
      </AnimatedButton>
    </LazyMotion>
  );
}
